import { type FC, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { LoginForm } from './components/LoginForm';
import { useZustandStore } from '../../appState/StoresProvider';
import { type LoginThemeConfig, LoginThemes, useDefaultLoginTheme } from '../../appState/hooks/useDefaultLoginTheme';
import { Spinner } from '../Spinner/Spinner';
import type { LoginSettings } from '../../appState/stateCreators/auth';
import { validateEmail } from '../../lib/string/validateEmail';
import React from 'react';
import { useAuthStore } from './useAuthStore';
import { saveSessionTenantId, getSessionTenantId } from '../../lib/storage/tenantSessionStorage';
import backgroundImage from './assets/cyberiq-login-bg.webp';
import { useValidatedOrigin } from '../../appState/hooks/useOrigin';
import { apiEndpoints } from '../../lib/apiEndpoints/apiEndpoints';

const LoginWrapper = ({ loginSettings, authCode }: { loginSettings: LoginSettings; authCode: string | null }) => {
  const isMounted = useRef(false);
  const { initialize, login, loginStatus, submitAuthCode } = useAuthStore((state) => state);
  const tenantId = useZustandStore('auth', (v) => v.tenantId);
  const validatedOrigin = useValidatedOrigin();
  saveSessionTenantId(tenantId);

  React.useEffect(() => {
    if (isMounted.current) {
      return;
    }
    isMounted.current = true;

    if (!tenantId) {
      throw new Error('Initialization error: Tenant identifier is missing.');
    }

    initialize({
      userPoolId: loginSettings.authenticationParameters?.userPoolId || '',
      userPoolClientId: loginSettings.authenticationParameters?.clientId || '',
      cognitoDomain: loginSettings.authenticationParameters?.authDomain || '',
      identityProvider: loginSettings.authenticationParameters?.idP || '',
      redirectUri: validatedOrigin,
      customScope: loginSettings.authenticationParameters?.accessScope || '',
      enableAutoLogin: loginSettings.authenticationMode === 'SSO' && !authCode,
      enableAutoTokenRefetch: true,
      tenantId: loginSettings.tenantCode,
    });

    if (authCode) {
      submitAuthCode(authCode);
    }
  }, [
    authCode,
    initialize,
    login,
    loginSettings.authenticationMode,
    loginSettings.authenticationParameters?.accessScope,
    loginSettings.authenticationParameters?.authDomain,
    loginSettings.authenticationParameters?.clientId,
    loginSettings.authenticationParameters?.idP,
    loginSettings.authenticationParameters?.userPoolId,
    loginSettings.tenantCode,
    loginStatus,
    submitAuthCode,
    tenantId,
    validatedOrigin,
  ]);

  return null;
};

export const Login: FC<{ authCode: string | null }> = ({ authCode }) => {
  const { requestOTP, login, loginStatus, isLoading, hasInitialized } = useAuthStore((state) => state);

  const waiting = useZustandStore('auth', (v) => v.isFetching);
  const loginSettings = useZustandStore('auth', (v) => 'loginSettings' in v && v.loginSettings);

  const [error, setError] = useState<string | undefined>(undefined);
  const defaultLoginTheme: LoginThemeConfig = useDefaultLoginTheme();

  const [hasRetried, setHasRetried] = useState(false);

  const handleLogin = (email: string, loginSettingsObj: LoginSettings, isRetry = false) => {
    if (loginSettingsObj.authenticationMode === 'OTPV2') {
      if (!email) {
        setError('Email cannot be empty.');
        return;
      }

      if (!validateEmail(email)) {
        setError('Please insert a valid email.');
        return;
      }

      requestOTP(email).catch(async (e) => {
        const errorType = e?.__type || e?.code || e?.name;
        const errorMessage = e?.message || e?.toString();
        if (errorType === 'NotAuthorizedException' && errorMessage === 'Incorrect username or password.') {
          setError('Please wait while we process your request..');
          try {
            const userCheckUrl = apiEndpoints.getAPIEndpoint('userCheck');
            const tenantId = getSessionTenantId();
            if (userCheckUrl && !hasRetried && !isRetry) {
              const headers: Record<string, string> = { 'Content-Type': 'application/json' };
              if (tenantId) {
                console.log('Adding OT-Customer-Id header for tenant:', tenantId);
                headers['OT-Customer-Id'] = tenantId;
              }
              const response = await fetch(userCheckUrl, {
                method: 'POST',
                headers,
                body: JSON.stringify({ email }),
              });
              const data = await response.json();

              if (data && data.status === 'ok') {
                setTimeout(() => {
                  setHasRetried(true);
                  setTimeout(
                    () => {
                      handleLogin(email, loginSettingsObj, true);
                    },
                    2000 + Math.floor(Math.random() * 1000),
                  ); // 2-3 seconds
                }, 10000); // 10 seconds delay before retry
                return;
              }
            }
          } catch (_err) {
            setError('An error occurred while processing your request. If the issue persists, please contact support.');
          }
        } else {
          setError('OTP request error.');
        }
      });
    } else if (loginSettingsObj.authenticationMode === 'SSO') {
      login();
    } else {
      setError('Unknown authentication method.');
    }
  };

  const theme = useTheme();

  return (
    <>
      {loginSettings && loginStatus !== 'loggedIn' && (
        <LoginWrapper loginSettings={loginSettings} authCode={authCode} />
      )}
      {hasInitialized && (
        <LoginContainer defaultLoginTheme={defaultLoginTheme} loginSettings={loginSettings}>
          <LoginContent className="login-content">
            {loginSettings && loginStatus !== 'loggedIn' && hasInitialized ? (
              <>
                <LoginForm
                  guidanceText={loginSettings.signInGuidanceText}
                  onSubmit={(email) => {
                    setHasRetried(false);
                    handleLogin(email, loginSettings);
                  }}
                  logoUrl={loginSettings.logoUrl}
                  errorText={error}
                  waiting={waiting || loginStatus !== 'loggedOut' || isLoading}
                  isEnabled={loginSettings.portalEnabled}
                  authenticationMode={loginSettings.authenticationMode === 'SSO' ? 'SSO' : 'OTP'}
                />
              </>
            ) : (
              <Spinner size={'32px'} color={theme.colors.prototype.primary} />
            )}
            {(loginSettings && loginSettings.signInTheme) === LoginThemes.splitScreen ? (
              <Image src={(loginSettings && loginSettings.backgroundUrl) || defaultLoginTheme.defaultSplitScreenURL} />
            ) : null}
          </LoginContent>
        </LoginContainer>
      )}
    </>
  );
};

const LoginContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'defaultLoginTheme' && prop !== 'loginSettings',
})<{ defaultLoginTheme: LoginThemeConfig; loginSettings: false | LoginSettings | null }>`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100%;
  background: ${({ defaultLoginTheme, loginSettings }) =>
    (loginSettings && loginSettings.signInTheme) === LoginThemes.fullBackground
      ? `url(${(loginSettings && loginSettings.backgroundUrl) || defaultLoginTheme.defaultFullBackgroundURL})`
      : ` url(${backgroundImage})`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  ${({ loginSettings }) =>
    (loginSettings && loginSettings.signInTheme) === LoginThemes.fullBackground &&
    `
      > .login-content {
        width: unset;
      }
    `}
`;

const LoginContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.prototype.card};
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  height: 504px;
  width: 800px;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    height: auto;
    width: unset;
  }
`;

const Image = styled.img`
  display: block;
  border-radius: 0 ${({ theme }) => theme.borderRadius.normal} ${({ theme }) => theme.borderRadius.normal} 0;
  width: 100%;
  max-width: 400px;
  height: 100%;
  object-fit: contain;

  @media (max-width: 768px) {
    max-height: 350px;
    max-width: 343px;
    border-radius: ${({ theme }) => theme.borderRadius.normal} ${({ theme }) => theme.borderRadius.normal} 0 0;
  }
`;
